<template>
  <b-modal
    id="bau_complement_form_modal"
    @show="onModalShow"
    @hide="$emit('close')"
    hide-header
    no-close-on-esc
    no-close-on-backdrop
    :modal-class="'rfm-fullscreen'"
    :dialog-class="'rfm-dialog'"
    :content-class="'rfm-content'"
    body-class="rfm-body"
  >
    <div class="d-flex align-items-center justify-content-between">
      <h3 class="h4 text-capitalize mb-0 font-weight-bold">
        {{ tf("bau_complement.list_title", "Complemento BAU") }}
      </h3>
      <b-badge pill variant="light-primary">
        {{ tf("custom_forms.state", "Estado") }}:
        {{ labelForState(currentState.toLowerCase()) }}
      </b-badge>
    </div>
    <hr class="mt-2 mb-2" />

    <div class="form-container">
      <div v-if="!readyToRenderForm" class="text-center my-2">
        <b-spinner label="Loading" variant="primary" />
      </div>

      <div v-else class="form-container-inner">
        <div v-if="loading" class="rfm-loading-overlay">
          <b-spinner label="Loading" variant="primary" />
        </div>
        <div class="rfm-grid">
          <div class="header-grid mb-3">
            <div class="info-card card">
              <div class="info-row">
                <span class="info-label text-body">
                  {{ tf("risk.reference_number", "Numero de Referencia") }}:
                </span>
                <span class="info-value text-body">{{ referenceNumber }}</span>
              </div>
              <div class="info-row">
                <span class="info-label text-body">
                  {{ tc("unit.title", 1, "Instalacion") }}:
                </span>
                <span class="info-value text-body">{{ unitNameDisplay }}</span>
              </div>
              <div class="info-row">
                <span class="info-label text-body">REMS:</span>
                <span class="info-value text-body">{{ rems }}</span>
              </div>
              <div class="info-row">
                <span class="info-label text-body">SIRH:</span>
                <span class="info-value text-body">{{ sirh }}</span>
              </div>
              <div v-if="templateNames" class="info-row">
                <span class="info-label text-body">
                  {{ tf("risk.template.title", "Plantilla") }}:
                </span>
                <span class="info-value text-body">{{ templateNames }}</span>
              </div>
            </div>

            <div class="history-card card">
              <div class="history-header d-flex align-items-center justify-content-between">
                <strong>{{ tf("custom_forms.history", "Historial") }}</strong>
                <small class="text-muted">
                  {{ sortedHistory.length }}
                  {{ tf("custom_forms.entries", "entradas") }}
                </small>
              </div>

              <div class="history-table" v-if="sortedHistory.length">
                <div
                  class="history-row history-row--head bg-transparent"
                  :style="historyGridStyle"
                >
                  <div class="col-state text-body">
                    {{ tf("custom_forms.state", "Estado") }}
                  </div>
                  <div class="col-user text-body">
                    {{ tf("custom_forms.user", "Usuario") }}
                  </div>
                  <div class="col-date text-body">
                    {{ tf("custom_forms.date", "Fecha") }}
                  </div>
                  <div v-if="hasHistoryComments" class="col-note text-body">
                    {{ tf("custom_forms.note", "Nota") }}
                  </div>
                </div>

                <div
                  v-for="status in visibleHistory"
                  :key="status.id"
                  class="history-row"
                  :style="historyGridStyle"
                >
                  <div class="col-state">
                    <b-badge pill :variant="badgeFor(status.name)" class="history-badge">
                      {{ labelForState(status.name) }}
                    </b-badge>
                  </div>
                  <div class="col-user">
                    <div class="history-user" :title="formatUser(status)">
                      <div class="user-name text-body">{{ getUserName(status) || "—" }}</div>
                      <div v-if="getUserEmail(status)" class="user-email text-body">
                        {{ getUserEmail(status) }}
                      </div>
                    </div>
                  </div>
                  <div class="col-date">
                    <small class="history-date text-body">
                      {{ formatDate(status.created_at) }}
                    </small>
                  </div>
                  <div v-if="hasHistoryComments" class="col-note">
                    <small class="text-muted" :title="getStatusComment(status)">
                      {{ getStatusComment(status) || "—" }}
                    </small>
                  </div>
                </div>
              </div>

              <div v-else class="text-muted small py-1">
                {{ tf("custom_forms.empty", "Sin movimientos") }}
              </div>

              <div v-if="sortedHistory.length > limitHistory" class="text-right mt-1">
                <b-button
                  variant="link"
                  size="sm"
                  class="p-0"
                  @click="showAllHistory = !showAllHistory"
                >
                  {{
                    showAllHistory
                      ? tf("custom_forms.show_less", "Ver menos")
                      : tf("custom_forms.show_all", "Ver todo el historial")
                  }}
                </b-button>
              </div>
            </div>

            <div v-if="toc.length" class="index-card card mt-2">
              <div class="index-header d-flex align-items-center justify-content-between">
                <h3 class="h5 text-capitalize mb-0 font-weight-bold">
                  {{ tf("risk.index", "Indice") }}
                </h3>
              </div>
              <ul class="toc-list">
                <li v-for="section in toc" :key="section.id">
                  <a
                    href="#"
                    class="toc-link text-body"
                    @click.prevent="scrollToAnchor(section.id)"
                  >
                    {{ section.label }}
                  </a>
                  <ul v-if="section.children && section.children.length" class="toc-sub">
                    <li v-for="subSection in section.children" :key="subSection.id">
                      <a
                        href="#"
                        class="toc-link text-body"
                        @click.prevent="scrollToAnchor(subSection.id)"
                      >
                        {{ subSection.label }}
                      </a>
                    </li>
                  </ul>
                </li>
              </ul>
            </div>

          </div>

          <div class="content-grid">
            <div class="main-stack">
              <build-simulacrum-form
                v-if="readyToRenderForm"
                :key="runnerKey"
                ref="runner"
                :simulacrum-id="bauComplementId"
                :template-snapshot="resolvedTemplateSnapshot"
                :template-source-ids="resolvedTemplateSourceIds"
                :templates-endpoint="'/v1/bau_complement_templates'"
                :form-answers-endpoint="'/v1/bau_complement_form_answers'"
                :form-by-entity-endpoint-template="
                  '/v1/bau_complement_form_answers/get-form-by-bau-complement/{id}'
                "
                :entity-id-field-name="'id_bau_complement'"
                :current-state="currentState"
                :is-reviewer="canReviewBauComplement"
                :can-edit="canEditForm"
                @submit="onAnswersSubmit"
                @submit-success="onAnswersSubmit"
                @submitError="onAnswersSubmitError"
                @toc="onToc"
                @model-changed="updateFormValidationState"
              />
            </div>
          </div>
        </div>
      </div>
    </div>

    <template #modal-footer="{ close }">
      <div class="w-100 rfm-footer">
        <div class="d-flex flex-row align-items-stretch justify-content-between">
          <div class="d-flex align-items-center flex-nowrap">
            <b-button
              v-if="showSaveButton"
              variant="primary"
              class="mr-2 mb-0"
              :disabled="loading"
              @click="onClickSave"
            >
              {{ tf("actions.save", "Guardar") }}
            </b-button>

            <template v-if="showReviewActions && canReviewBauComplement">
              <b-button
                variant="success"
                class="mr-1 mb-0"
                :disabled="loading"
                @click="updateBauComplementStatus('approved')"
              >
                {{ $t("actions.approve") }}
              </b-button>
              <b-button
                variant="danger"
                class="mr-1 mb-0"
                :disabled="loading"
                @click="updateBauComplementStatus('rejected')"
              >
                {{ $t("actions.reject") }}
              </b-button>
            </template>

            <template v-else-if="canReassignAfterRejected">
              <div class="reviewer-inline mr-2 mb-1">
                <v-select
                  v-model="selectedReassign"
                  :options="reassignableOptions"
                  :reduce="(user) => user.value"
                  :clearable="false"
                  :disabled="loading"
                  :dir="$store.state.appConfig.isRTL ? 'rtl' : 'ltr'"
                  :placeholder="
                    tf('custom_forms.select_reassigner', 'Selecciona usuario para reasignar')
                  "
                  input-id="reassign-after-reject"
                  class="select-opens-up"
                />
              </div>
              <b-button
                variant="warning"
                class="mr-1 mb-0"
                :disabled="loading || !selectedReassign"
                @click="updateBauComplementStatus('reassigned', selectedReassign)"
              >
                {{ tf("custom_forms.reassign", "Reasignar") }}
              </b-button>
            </template>

            <template v-else-if="canSendToReview">
              <div class="reviewer-inline mr-2 mb-1">
                <v-select
                  v-model="selectedReviewer"
                  :options="userOptions"
                  :reduce="(user) => user.value"
                  :clearable="false"
                  :disabled="loading"
                  :dir="$store.state.appConfig.isRTL ? 'rtl' : 'ltr'"
                  :placeholder="tf('custom_forms.select_reviewer', 'Selecciona usuario revisor')"
                  input-id="reviewer"
                  class="select-opens-up"
                />
              </div>
              <b-button
                variant="warning"
                class="mr-1 mb-0"
                :disabled="loading || !selectedReviewer"
                @click="updateBauComplementStatus('in_review', selectedReviewer)"
              >
                {{ tf("custom_forms.send_to_review", "Enviar a revision") }}
              </b-button>
            </template>

            <template v-else-if="canSpecialReassign">
              <div class="reviewer-inline mr-2 mb-1">
                <v-select
                  v-model="selectedReassign"
                  :options="reassignableOptions"
                  :reduce="(user) => user.value"
                  :clearable="false"
                  :disabled="loading"
                  :dir="$store.state.appConfig.isRTL ? 'rtl' : 'ltr'"
                  :placeholder="
                    tf('custom_forms.select_reassigner', 'Selecciona usuario para reasignar')
                  "
                  input-id="special-reassign"
                  class="select-opens-up"
                />
              </div>
              <b-button
                variant="warning"
                class="mr-1 mb-0"
                :disabled="loading || !selectedReassign"
                @click="updateBauComplementStatus('reassigned', selectedReassign)"
              >
                {{ tf("custom_forms.reassign", "Reasignar") }}
              </b-button>
            </template>
          </div>

          <div class="d-flex align-items-center">
            <b-button variant="danger" class="mb-0" :disabled="loading" @click="close()">
              {{ tf("custom_forms.close", "Cerrar") }}
            </b-button>
          </div>
        </div>
      </div>
    </template>
  </b-modal>
</template>

<script>
import { BModal, BSpinner, BButton, BBadge } from "bootstrap-vue";
import vSelect from "vue-select";
import axios from "@axios";
import ToastificationContent from "@core/components/toastification/ToastificationContent.vue";
import ability from "@/libs/acl/ability";
import BuildSimulacrumForm from "@/components/BuildSimulacrumForm";

export default {
  name: "ShowBauComplementFormModal",
  components: { BModal, BSpinner, BButton, BBadge, vSelect, BuildSimulacrumForm },
  props: {
    bauComplementId: { type: [Number, String], required: true },
    templateSnapshot: { type: Array, default: () => [] },
    templateSourceIds: { type: Array, default: () => [] },
  },
  data() {
    return {
      loading: false,
      referenceNumber: "",
      unitNameDisplay: "",
      templateNames: "",
      currentState: "in_progress",
      readyToRenderForm: false,
      runnerKey: 0,
      didBootstrap: false,
      creating: false,
      statuses: [],
      showAllHistory: false,
      limitHistory: 6,
      selectedReviewer: null,
      userOptions: [],
      reassignableOptions: [],
      selectedReassign: null,
      toc: [],
      _pendingSaveResolve: null,
      lastStatusUserId: null,
      rems: "",
      sirh: "",
      formValidationState: true,
      resolvedTemplateSnapshot: [],
      resolvedTemplateSourceIds: [],
    };
  },
  watch: {
    readyToRenderForm(newValue) {
      if (newValue) this.updateFormValidationState();
    },
    bauComplementId: {
      immediate: true,
      handler() {
        this.loadTemplateContext();
      },
    },
    templateSnapshot: {
      immediate: true,
      handler() {
        this.loadTemplateContext();
      },
    },
    templateSourceIds: {
      immediate: true,
      handler() {
        this.loadTemplateContext();
      },
    },
  },
  computed: {
    canSendToReview() {
      const state = String(this.currentState || "").toLowerCase();
      if (state !== "in_progress" && state !== "reassigned") return false;
      return this.isAssignedToCurrentAuthorState();
    },
    canReviewBauComplement() {
      if (!this.can("review_bau_complement", "BauComplement")) return false;
      const state = String(this.currentState || "").toLowerCase();
      if (state !== "in_review") return false;
      let assignedId = this.lastStatusUserId;
      let assignedEmail = null;
      const list = Array.isArray(this.sortedHistory) ? this.sortedHistory : [];
      const lastReview = list.find(
        (status) => String(status?.name || "").toLowerCase() === "in_review"
      );
      if (lastReview) {
        assignedId =
          assignedId ||
          lastReview?.id_user ||
          lastReview?.user_id ||
          lastReview?.reviewer_id ||
          lastReview?.id_reviewer ||
          lastReview?.user?.id ||
          lastReview?.userId ||
          null;
        assignedEmail = lastReview?.user?.email || lastReview?.email || null;
      }
      const currentUserId = this.getLocalUserId();
      const currentUserEmail = this.getLocalUserEmail();
      const isIdMatch =
        Number(assignedId) > 0 &&
        Number(currentUserId) > 0 &&
        Number(assignedId) === Number(currentUserId);
      const isEmailMatch =
        assignedEmail &&
        currentUserEmail &&
        String(assignedEmail).toLowerCase() === String(currentUserEmail).toLowerCase();
      return !!(isIdMatch || isEmailMatch);
    },
    showReviewActions() {
      return String(this.currentState || "").toLowerCase() === "in_review";
    },
    canReassignAfterRejected() {
      const state = String(this.currentState || "").toLowerCase();
      return state === "rejected" && this.can("review_bau_complement", "BauComplement");
    },
    canSpecialReassign() {
      const state = String(this.currentState || "").toLowerCase();
      if (state !== "in_progress" && state !== "reassigned" && state !== "in_review") {
        return false;
      }
      return this.hasSpecialReasingForBauComplement();
    },
    sortedHistory() {
      return [...this.statuses].sort(
        (firstStatus, secondStatus) =>
          new Date(secondStatus.created_at) - new Date(firstStatus.created_at)
      );
    },
    visibleHistory() {
      return this.showAllHistory
        ? this.sortedHistory
        : this.sortedHistory.slice(0, this.limitHistory);
    },
    hasHistoryComments() {
      return this.statuses.some((status) => !!this.getStatusComment(status));
    },
    historyGridStyle() {
      const base = "max-content minmax(140px, 1fr) minmax(190px, max-content)";
      return {
        gridTemplateColumns: this.hasHistoryComments
          ? `${base} minmax(140px, 1fr)`
          : base,
      };
    },
    canEditForm() {
      const state = String(this.currentState || "").toLowerCase();
      if (
        ["approved", "rejected", "not_approved", "scheduled", "canceled"].includes(state)
      ) {
        return false;
      }
      if (state === "in_review") return false;
      if (state === "in_progress" || state === "reassigned") {
        return this.isAssignedToCurrentAuthorState();
      }
      return false;
    },
    showSaveButton() {
      const state = String(this.currentState || "").toLowerCase();
      if (state === "approved" || state === "rejected" || state === "canceled") return false;
      if (state === "in_review") {
        let assignedId = this.lastStatusUserId;
        let assignedEmail = null;
        const list = Array.isArray(this.sortedHistory) ? this.sortedHistory : [];
        const lastReview = list.find(
          (status) => String(status?.name || "").toLowerCase() === "in_review"
        );
        if (lastReview) {
          assignedId =
            assignedId ||
            lastReview?.id_user ||
            lastReview?.user_id ||
            lastReview?.reviewer_id ||
            lastReview?.id_reviewer ||
            lastReview?.user?.id ||
            lastReview?.userId ||
            null;
          assignedEmail = lastReview?.user?.email || lastReview?.email || null;
        }
        const currentUserId = this.getLocalUserId();
        const currentUserEmail = this.getLocalUserEmail && this.getLocalUserEmail();
        if (!assignedId && !assignedEmail) return false;
        if (!currentUserId && !currentUserEmail) return false;
        const isIdMatch =
          Number(assignedId) > 0 &&
          Number(currentUserId) > 0 &&
          Number(assignedId) === Number(currentUserId);
        const isEmailMatch =
          assignedEmail &&
          currentUserEmail &&
          String(assignedEmail).toLowerCase() === String(currentUserEmail).toLowerCase();
        return !!(isIdMatch || isEmailMatch);
      }
      if (state === "in_progress" || state === "reassigned") {
        return this.isAssignedToCurrentAuthorState();
      }
      return false;
    },
    isFormValid() {
      return this.formValidationState;
    },
  },
  created() {
    this.loadReviewerOptions();
    this.loadReassignableOptions();
  },
  methods: {
    async onModalShow() {
      this.readyToRenderForm = false;
      this.runnerKey += 1;
      if (!Array.isArray(this.userOptions) || !this.userOptions.length) {
        await this.loadReviewerOptions();
      }
      await this.loadTemplateContext();
      await this.loadBauComplement();
      this.readyToRenderForm = (await this.ensureAnswerExists()) !== false;
      this.$nextTick(() => {
        this.$refs.runner?.reloadForm?.();
        this.updateFormValidationState();
      });
    },
    hasSpecialReasingForBauComplement() {
      try {
        return !!(this.$can && this.$can("special_reasing", "BauComplement"));
      } catch (_) {
        return false;
      }
    },
    can(action, subject) {
      try {
        return typeof this.$can === "function"
          ? this.$can(action, subject)
          : ability.can(action, subject);
      } catch (error) {
        return ability.can(action, subject);
      }
    },
    onToc(toc) {
      this.toc = Array.isArray(toc) ? toc : [];
    },
    scrollToAnchor(id) {
      const safeId = window.CSS && CSS.escape ? CSS.escape(id) : id;
      const modalElement = document.querySelector(".modal.show");
      if (!modalElement) return;
      const container =
        modalElement.querySelector(".form-container") ||
        modalElement.querySelector(".modal-body") ||
        modalElement.querySelector(".modal-content") ||
        modalElement;
      const target =
        modalElement.querySelector(`.rfm-anchor.rfm-aid-${safeId}`) ||
        modalElement.querySelector(`#${safeId}`) ||
        null;
      if (!target) return;
      const containerRect = container.getBoundingClientRect();
      const targetRect = target.getBoundingClientRect();
      const top =
        targetRect.top -
        containerRect.top +
        container.scrollTop -
        container.clientHeight / 2 +
        targetRect.height / 2;
      container.scrollTo({ top: Math.max(top, 0), behavior: "smooth" });
    },
    updateFormValidationState() {
      this.$nextTick(() => {
        if (!this.$refs?.runner) {
          this.formValidationState = true;
          return;
        }
        if (typeof this.$refs.runner.isFormValid === "function") {
          const isValid = this.$refs.runner.isFormValid();
          if (this.formValidationState !== isValid) {
            this.formValidationState = isValid;
          }
          return;
        }
        this.formValidationState = true;
      });
    },
    buildUserOptionLabel(user) {
      const parts = [user.name, user.user, user.email].filter(Boolean);
      return parts.join(" | ");
    },
    async loadReviewerOptions() {
      const mapUsers = (users) =>
        (users || []).map((user) => ({
          value: user.id,
          label: this.buildUserOptionLabel(user),
        }));
      try {
        const { data } = await axios.get("/v1/bau-complements/getReviewers");
        const list = data?.data || data || [];
        if (Array.isArray(list) && list.length) {
          this.userOptions = mapUsers(list);
          return;
        }
      } catch (_) {}
      this.userOptions = [];
    },
    async loadReassignableOptions() {
      try {
        const { data } = await axios.get("/v1/bau-complements/getReassignable");
        const list = data?.data || data || [];
        this.reassignableOptions = (list || []).map((user) => ({
          value: user.id,
          label: this.buildUserOptionLabel(user),
        }));
      } catch (_) {
        this.reassignableOptions = [];
      }
    },
    tf(key, fallback = "") {
      try {
        const out = this.$t ? this.$t(key) : key;
        return typeof out === "string" && out === key ? fallback : out;
      } catch (error) {
        return fallback;
      }
    },
    tc(key, count = 1, fallback = "") {
      try {
        const out = this.$tc ? this.$tc(key, count) : key;
        return typeof out === "string" && out === key ? fallback : out;
      } catch (error) {
        return fallback;
      }
    },
    humanize(str = "") {
      return String(str)
        .replace(/_/g, " ")
        .replace(/\b\w/g, (char) => char.toUpperCase());
    },
    labelForState(name) {
      const raw = String(name || "");
      const normalized = raw
        .trim()
        .toLowerCase()
        .replace(/[-\s]+/g, "_");
      const aliases = { canceled: "cancelled" };
      const canonical = aliases[normalized] || normalized;
      const tryKeys = [
        `procedure_states.${canonical}`,
        `procedure_states.value.${canonical}`,
      ];
      for (const key of tryKeys) {
        const value = this.$t ? this.$t(key) : key;
        if (typeof value === "string" && value !== key) return value;
      }
      return this.humanize(canonical);
    },
    async loadBauComplement(manageLoading = true) {
      if (!this.bauComplementId) return;
      try {
        if (manageLoading) this.loading = true;
        const { data } = await axios.get(`/v1/bau-complements/${this.bauComplementId}`);
        const bauComplement = data && data.data ? data.data : data;
        this.referenceNumber = bauComplement.reference_number || "";
        this.unitNameDisplay = bauComplement.unit?.description || "";
        this.rems = bauComplement.rems ?? bauComplement.unit?.rems ?? "";
        this.sirh = bauComplement.sirh ?? bauComplement.unit?.sirh ?? "";
        this.currentState = bauComplement.last_status?.name || "in_progress";
        this.statuses = Array.isArray(bauComplement.statuses) ? bauComplement.statuses : [];
        this.lastStatusUserId =
          bauComplement.last_status?.id_user ||
          bauComplement.last_status?.user_id ||
          bauComplement.last_status?.reviewer_id ||
          bauComplement.last_status?.id_reviewer ||
          bauComplement.last_status?.user?.id ||
          null;
        if (String(this.currentState).toLowerCase() === "rejected") {
          this.loadReassignableOptions();
        } else {
          this.selectedReassign = null;
        }
        if (bauComplement.formAnswer?.template_sources?.length) {
          this.templateNames = bauComplement.formAnswer.template_sources
            .map((source) => (source && source.name ? source.name : null))
            .filter(Boolean)
            .join(", ");
          return;
        }
        if (this.resolvedTemplateSourceIds?.length) {
          const sources = await this.resolveTemplateSourcesByIds(this.resolvedTemplateSourceIds);
          this.templateNames = sources.map((source) => source.name).join(", ");
          return;
        }
        this.templateNames = "";
      } catch (_) {
      } finally {
        if (manageLoading) this.loading = false;
      }
    },
    async resolveTemplateSourcesByIds(ids = []) {
      if (!Array.isArray(ids) || !ids.length) return [];
      try {
        const { data } = await axios.get("/v1/bau_complement_templates");
        const all = data.data || [];
        const selectedIds = new Set(ids.map(Number));
        return all
          .filter((template) => selectedIds.has(Number(template.id)))
          .map((template) => ({ id: template.id, name: template.name }));
      } catch (_) {
        return [];
      }
    },
    async ensureAnswerExists() {
      if (this.didBootstrap || this.creating) return true;
      try {
        await axios.get(
          `/v1/bau_complement_form_answers/get-form-by-bau-complement/${this.bauComplementId}`
        );
        this.didBootstrap = true;
        return true;
      } catch (lookupError) {
        if (lookupError?.response?.status !== 404) {
          this.$toast?.error?.(
            lookupError?.response?.data?.message ||
              this.tf("custom_forms.cannot_load_form", "No se pudo cargar el formulario")
          );
          return false;
        }

        this.creating = true;
        try {
          const formData = new FormData();
          formData.append("id_bau_complement", this.bauComplementId);
          formData.append("data", JSON.stringify({}));
          formData.append(
            "template_snapshot",
            JSON.stringify(this.resolvedTemplateSnapshot || [])
          );
          let sourcesToSend = [];
          if (this.resolvedTemplateSourceIds && this.resolvedTemplateSourceIds.length) {
            sourcesToSend = await this.resolveTemplateSourcesByIds(
              this.resolvedTemplateSourceIds
            );
          }
          formData.append("template_sources", JSON.stringify(sourcesToSend));
          await axios.post("/v1/bau_complement_form_answers", formData, {
            headers: { "Content-Type": "multipart/form-data" },
          });
          if (sourcesToSend.length) {
            this.templateNames = sourcesToSend.map((source) => source.name).join(", ");
          } else if (this.resolvedTemplateSnapshot?.length) {
            this.templateNames = this.tf("risk.template.composed", "Composed");
          }
          this.didBootstrap = true;
          await this.loadBauComplement(false);
          return true;
        } catch (createError) {
          this.$toast?.error?.(
            createError?.response?.data?.message ||
              this.tf("custom_forms.cannot_start_form", "No se pudo iniciar el formulario")
          );
          return false;
        } finally {
          this.creating = false;
        }
      }
    },
    async updateBauComplementStatus(newStatus, toUserId = null) {
      const guardedStatuses = ["approved", "rejected"];
      if (guardedStatuses.includes(newStatus) && !this.canReviewBauComplement) return;
      if (!this.bauComplementId) return;
      if (newStatus === "in_review") {
        if (!toUserId) {
          if (this.$toast) {
            this.$toast.error(
              this.tf("custom_forms.select_reviewer", "Selecciona usuario revisor")
            );
          }
          return;
        }
        const canSend = await this.$refs.runner?.validateBeforeReview?.();
        if (!canSend || !canSend.ok) {
          const missing = canSend && canSend.missing ? canSend.missing : [];
          const missingCount = missing.length;
          if (missingCount > 0 && this.$toast) {
            const lastMissing = missing[missing.length - 1];
            const lastFieldName = lastMissing?.name || "";
            const message =
              missingCount === 1
                ? `${this.tf(
                    "bau_complement.one_required_field_missing",
                    "Falta 1 campo obligatorio"
                  )}: ${lastFieldName}`
                : `${this.tf(
                    "bau_complement.multiple_required_fields_missing",
                    `Faltan ${missingCount} campos obligatorios`
                  )}. Ultima: ${lastFieldName}`;
            this.$toast.error(message);
          }
          return;
        }
      }
      try {
        this.loading = true;
        const payload = { status: newStatus };
        if (toUserId) payload.id_user = toUserId;
        await axios.post(`/v1/bau-complements/update_status/${this.bauComplementId}`, payload);
        this.currentState = newStatus;
        this.$toast?.success?.(
          `${this.tf("custom_forms.state", "Estado")}: ${this.labelForState(
            newStatus.toLowerCase()
          )}`
        );
        await this.loadBauComplement(false);
        await this.ensureAnswerExists();
        this.$nextTick(() => this.$refs.runner?.reloadForm?.());
      } catch (_) {
        this.$toast?.error?.(
          this.tf("custom_forms.update_state_error", "No se pudo actualizar el estado")
        );
      } finally {
        this.loading = false;
      }
    },
    onAnswersSubmit() {
      if (typeof this._pendingSaveResolve === "function") {
        this._pendingSaveResolve({ success: true });
      }
    },
    onAnswersSubmitError() {
      if (typeof this._pendingSaveResolve === "function") {
        this._pendingSaveResolve({ success: false });
      }
      this.loading = false;
      this.$toast && this.$toast.error(this.tf("risk.save_failed", "No se pudo guardar"));
    },
    async onClickSave() {
      if (!this.bauComplementId) return;
      try {
        this.loading = true;
        const waitForSubmit = new Promise((resolve) => {
          this._pendingSaveResolve = resolve;
        });
        const safetyTimeoutMs = 120000;
        const safetyTimeout = new Promise((resolve) =>
          setTimeout(() => resolve(undefined), safetyTimeoutMs)
        );
        let triggered = false;
        if (this.$refs?.runner) {
          if (typeof this.$refs.runner.submitForm === "function") {
            this.$refs.runner.submitForm();
            triggered = true;
          } else if (this.$refs.runner.$refs?.form?.submit) {
            this.$refs.runner.$refs.form.submit();
            triggered = true;
          }
        }
        if (!triggered && typeof this._pendingSaveResolve === "function") {
          this._pendingSaveResolve({ success: false });
        }
        const runner = this.$refs?.runner || null;
        const waitRunner = runner
          ? this.waitForRunnerIdle(runner, safetyTimeoutMs)
          : Promise.resolve();
        const saveResult = await Promise.race([waitForSubmit, waitRunner, safetyTimeout]);
        if (saveResult && saveResult.success === true) {
          this.didBootstrap = false;
          await this.loadBauComplement(false);
          await this.ensureAnswerExists();
          if (typeof this.$refs.runner?.reloadForm === "function") {
            await this.$refs.runner.reloadForm();
          }
          const message = this.tf("custom_forms.answers_saved", "Respuestas guardadas");
          this.$toast &&
            this.$toast({
              component: ToastificationContent,
              props: { title: message, icon: "CheckIcon", variant: "success" },
            });
        } else if (!(saveResult && saveResult.success === false)) {
          this.didBootstrap = false;
          await this.loadBauComplement(false);
          await this.ensureAnswerExists();
          if (typeof this.$refs.runner?.reloadForm === "function") {
            await this.$refs.runner.reloadForm();
          }
        }
      } catch (_) {
        this._pendingSaveResolve = null;
        this.$toast &&
          this.$toast.error(this.tf("risk.save_failed", "No se pudo guardar"));
      } finally {
        this._pendingSaveResolve = null;
        this.loading = false;
      }
    },
    waitForRunnerIdle(runner, timeoutMs = 60000) {
      return new Promise((resolve) => {
        try {
          if (!runner || runner.loading === false) return resolve();
          let settled = false;
          const done = () => {
            if (!settled) {
              settled = true;
              if (typeof unwatch === "function") unwatch();
              clearTimeout(timeoutId);
              resolve();
            }
          };
          const unwatch = runner.$watch("loading", (value) => {
            if (value === false) done();
          });
          const timeoutId = setTimeout(done, Math.max(1000, Number(timeoutMs) || 60000));
        } catch (_) {
          resolve();
        }
      });
    },
    formatDate(iso) {
      if (!iso) return "";
      try {
        const date = new Date(iso);
        return date.toLocaleString();
      } catch (_) {
        return iso;
      }
    },
    getUserName(status) {
      return status?.user_name || status?.user?.name || "";
    },
    getUserEmail(status) {
      const user = status?.user;
      if (!user) return "";
      if (typeof user === "string") return user;
      return user?.email || "";
    },
    formatUser(status) {
      const name = this.getUserName(status);
      const email = this.getUserEmail(status);
      return [name, email].filter(Boolean).join(" | ");
    },
    getStatusComment(status) {
      return (
        status?.comment ||
        status?.comments ||
        status?.note ||
        status?.notes ||
        status?.description ||
        ""
      );
    },
    getLocalUserId() {
      try {
        const raw = localStorage.getItem("user") || localStorage.getItem("userData") || "";
        if (!raw) return null;
        const obj = JSON.parse(raw);
        const candidates = [
          obj?.id,
          obj?.user?.id,
          obj?.data?.id,
          obj?.user_id,
          obj?.uid,
        ];
        for (const candidate of candidates) {
          const num = Number(candidate);
          if (Number.isFinite(num) && num > 0) return num;
        }
        return null;
      } catch (_) {
        return null;
      }
    },
    getLocalUserEmail() {
      try {
        const raw = localStorage.getItem("user") || localStorage.getItem("userData") || "";
        if (!raw) return null;
        const obj = JSON.parse(raw);
        const candidates = [obj?.email, obj?.user?.email, obj?.data?.email];
        for (const candidate of candidates) {
          if (candidate && typeof candidate === "string" && candidate.includes("@")) {
            return candidate;
          }
        }
        return null;
      } catch (_) {
        return null;
      }
    },
    isAssignedToCurrentAuthorState() {
      const state = String(this.currentState || "").toLowerCase();
      if (!["in_progress", "reassigned"].includes(state)) return false;
      let assignedId = this.lastStatusUserId;
      let assignedEmail = null;
      const list = Array.isArray(this.sortedHistory) ? this.sortedHistory : [];
      const lastForState = list.find(
        (status) => String(status?.name || "").toLowerCase() === state
      );
      if (lastForState) {
        assignedId =
          assignedId ||
          lastForState?.id_user ||
          lastForState?.user_id ||
          lastForState?.owner_id ||
          lastForState?.id_owner ||
          lastForState?.reviewer_id ||
          lastForState?.id_reviewer ||
          lastForState?.user?.id ||
          lastForState?.userId ||
          null;
        assignedEmail = lastForState?.user?.email || lastForState?.email || null;
      }
      const currentUserId = this.getLocalUserId();
      const currentUserEmail = this.getLocalUserEmail && this.getLocalUserEmail();
      const isIdMatch =
        Number(assignedId) > 0 &&
        Number(currentUserId) > 0 &&
        Number(assignedId) === Number(currentUserId);
      const isEmailMatch =
        assignedEmail &&
        currentUserEmail &&
        String(assignedEmail).toLowerCase() === String(currentUserEmail).toLowerCase();
      return !!(isIdMatch || isEmailMatch);
    },
    badgeFor(name) {
      const map = {
        in_progress: "primary",
        in_review: "warning",
        approved: "success",
        rejected: "danger",
        scheduled: "info",
        canceled: "canceled",
        reassigned: "reassigned",
      };
      const key = String(name || "").toLowerCase();
      return map[key] || "secondary";
    },
    getLocalContextStorageKey() {
      return `bauComplementFormContext:${this.bauComplementId}`;
    },
    saveLocalContext(context) {
      try {
        localStorage.setItem(this.getLocalContextStorageKey(), JSON.stringify(context));
      } catch (_) {}
    },
    readLocalContext() {
      try {
        const rawValue = localStorage.getItem(this.getLocalContextStorageKey());
        if (!rawValue) return null;
        const parsedValue = JSON.parse(rawValue);
        if (!parsedValue || typeof parsedValue !== "object") return null;
        return parsedValue;
      } catch (_) {
        return null;
      }
    },
    getAnswerPayload(responseData) {
      if (responseData?.data?.data) return responseData.data.data;
      if (responseData?.data) return responseData.data;
      return null;
    },
    getEntityPayload(responseData) {
      if (responseData?.data?.data) return responseData.data.data;
      if (responseData?.data) return responseData.data;
      return responseData || null;
    },
    normalizeTemplateSources(rawSources) {
      if (!Array.isArray(rawSources)) return [];
      return rawSources
        .map((source) => {
          if (source && typeof source === "object") {
            return source.id != null ? Number(source.id) : null;
          }
          return Number(source);
        })
        .filter((sourceId) => Number.isFinite(sourceId));
    },
    deepClone(value) {
      return JSON.parse(JSON.stringify(value));
    },
    namespaceFieldIds(templateId, fields) {
      return (Array.isArray(fields) ? fields : []).map((field) => {
        const normalizedField = this.deepClone(field);
        const oldId = normalizedField.item_id;
        normalizedField.item_id = `${templateId}:${oldId}`;
        if (Array.isArray(normalizedField.show_if)) {
          normalizedField.show_if = normalizedField.show_if.map((condition) =>
            condition && condition.field
              ? { ...condition, field: `${templateId}:${condition.field}` }
              : condition
          );
        }
        return normalizedField;
      });
    },
    buildSnapshotFromTemplates(templates) {
      const snapshot = [];
      (templates || []).forEach((template) => {
        const fields = this.namespaceFieldIds(template.id, template.data);
        fields.forEach((field) => snapshot.push(field));
      });
      return snapshot;
    },
    async fetchTemplatesFallback(entity) {
      try {
        const templatesResponse = await axios.get("/v1/bau_complement_templates");
        const allTemplates = Array.isArray(templatesResponse.data?.data)
          ? templatesResponse.data.data
          : [];
        if (!allTemplates.length) return { snapshot: [], sourceIds: [] };
        const tenantIdFromEntity =
          entity?.id_tenant ||
          entity?.unit?.id_tenant ||
          entity?.unit?.router?.id_tenant ||
          null;
        const tenantTemplates = tenantIdFromEntity
          ? allTemplates.filter(
              (template) => Number(template.id_tenant) === Number(tenantIdFromEntity)
            )
          : [];
        const selectedTemplates = tenantTemplates.length ? tenantTemplates : allTemplates;
        const sourceIds = selectedTemplates
          .map((template) => Number(template.id))
          .filter((templateId) => Number.isFinite(templateId));
        const snapshot = this.buildSnapshotFromTemplates(selectedTemplates);
        return { snapshot, sourceIds };
      } catch (_) {
        return { snapshot: [], sourceIds: [] };
      }
    },
    applyResolvedContext(snapshot, sourceIds) {
      this.resolvedTemplateSnapshot = Array.isArray(snapshot) ? snapshot : [];
      this.resolvedTemplateSourceIds = Array.isArray(sourceIds) ? sourceIds : [];
    },
    getInitialContext() {
      return {
        snapshot: Array.isArray(this.templateSnapshot) ? this.templateSnapshot : [],
        sourceIds: Array.isArray(this.templateSourceIds) ? this.templateSourceIds : [],
      };
    },
    async loadTemplateContext() {
      const initialContext = this.getInitialContext();
      let snapshot = initialContext.snapshot;
      let sourceIds = initialContext.sourceIds;
      if (this.bauComplementId == null) {
        this.applyResolvedContext(snapshot, sourceIds);
        return;
      }
      try {
        const answersResponse = await axios.get(
          `/v1/bau_complement_form_answers/get-form-by-bau-complement/${this.bauComplementId}`
        );
        const formAnswer = this.getAnswerPayload(answersResponse.data);
        if (
          Array.isArray(formAnswer?.template_snapshot) &&
          formAnswer.template_snapshot.length
        ) {
          snapshot = formAnswer.template_snapshot;
        }
        const normalizedSources = this.normalizeTemplateSources(formAnswer?.template_sources);
        if (normalizedSources.length) sourceIds = normalizedSources;
      } catch (_) {}
      let loadedEntity = null;
      if (!snapshot.length || !sourceIds.length) {
        try {
          const entityResponse = await axios.get(`/v1/bau-complements/${this.bauComplementId}`);
          const entity = this.getEntityPayload(entityResponse.data);
          loadedEntity = entity;
          if (
            !snapshot.length &&
            Array.isArray(entity?.formAnswer?.template_snapshot) &&
            entity.formAnswer.template_snapshot.length
          ) {
            snapshot = entity.formAnswer.template_snapshot;
          }
          if (!sourceIds.length) {
            const normalizedSources = this.normalizeTemplateSources(
              entity?.formAnswer?.template_sources
            );
            if (normalizedSources.length) sourceIds = normalizedSources;
          }
        } catch (_) {}
      }
      if (!snapshot.length || !sourceIds.length) {
        const localContext = this.readLocalContext();
        if (!snapshot.length && Array.isArray(localContext?.snapshot)) {
          snapshot = localContext.snapshot;
        }
        if (!sourceIds.length && Array.isArray(localContext?.sources)) {
          sourceIds = this.normalizeTemplateSources(localContext.sources);
        }
      }
      if (!snapshot.length || !sourceIds.length) {
        const fallback = await this.fetchTemplatesFallback(loadedEntity);
        if (!snapshot.length && Array.isArray(fallback.snapshot) && fallback.snapshot.length) {
          snapshot = fallback.snapshot;
        }
        if (!sourceIds.length && Array.isArray(fallback.sourceIds) && fallback.sourceIds.length) {
          sourceIds = fallback.sourceIds;
        }
      }
      this.applyResolvedContext(snapshot, sourceIds);
      if (snapshot.length || sourceIds.length) {
        this.saveLocalContext({
          snapshot,
          sources: sourceIds,
          savedAt: Date.now(),
        });
      }
    },
  },
};
</script>

<style>
.rfm-fullscreen {
  padding: 0 !important;
}
.rfm-dialog {
  width: 100vw;
  max-width: 100vw;
  height: 100vh;
  margin: 0;
}
.rfm-content {
  min-height: 100vh;
  height: 100vh;
  display: flex;
  flex-direction: column;
  border: 0;
  border-radius: 0;
}
.rfm-body {
  display: flex;
  flex-direction: column;
  height: 100%;
  min-height: 0;
  overflow: hidden;
}
</style>

<style scoped>
.form-container {
  flex: 1 1 auto;
  min-height: 0;
  overflow: auto;
}
.form-container-inner {
  position: relative;
  min-height: 100%;
}
.rfm-loading-overlay {
  position: absolute;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(255, 255, 255, 0.85);
  z-index: 10;
}
.rfm-grid {
  display: grid;
  grid-template-columns: 1.1fr 1.9fr;
  grid-template-areas: "left right";
  gap: 12px;
  align-items: start;
}
.rfm-grid > .header-grid {
  grid-area: left;
  position: sticky;
  top: 0;
  align-self: start;
}
.rfm-grid > .content-grid {
  grid-area: right;
}
@media (max-width: 992px) {
  .rfm-grid {
    grid-template-columns: 1fr;
    grid-template-areas: "right" "left";
  }
  .rfm-grid > .header-grid {
    position: static;
  }
  .index-card {
    max-height: none;
  }
}
.header-grid {
  display: grid;
  grid-template-columns: 1fr;
  gap: 12px;
}
.info-card {
  background: #f9fafb;
  border: 1px solid #eceff3;
  border-radius: 8px;
  padding: 10px 12px;
}
.info-row {
  display: flex;
  gap: 6px;
  margin-bottom: 4px;
}
.info-label {
  font-weight: 600;
  color: #444;
}
.info-value {
  color: #111;
}
.history-card {
  background: #fff;
  border: 1px solid #eceff3;
  border-radius: 8px;
  padding: 12px;
  overflow: hidden;
}
.history-header {
  margin-bottom: 8px;
}
.history-table {
  width: 100%;
}
.history-row {
  display: grid;
  grid-column-gap: 8px;
  column-gap: 8px;
  align-items: center;
  padding: 10px 0;
  border-bottom: 1px dashed #eee;
}
.history-row:last-child {
  border-bottom: 0;
}
.history-row--head {
  border-bottom: 1px solid #e9ecef;
  font-weight: 600;
  color: #444;
  padding-top: 0;
  padding-bottom: 8px;
}
.col-state {
  justify-self: start;
}
.col-user {
  justify-self: start;
  min-width: 0;
}
.col-date {
  justify-self: end;
  white-space: nowrap;
  overflow: visible;
}
.history-row--head > div,
.history-row > .col-user {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.col-note {
  white-space: normal;
  overflow: hidden;
}
.history-badge {
  display: inline-flex;
  align-items: center;
}
.history-date {
  font-size: 12px;
  color: #6c757d;
  font-variant-numeric: tabular-nums;
}
.content-grid {
  display: block;
}
.index-card {
  background: #fff;
  border: 1px solid #eceff3;
  border-radius: 8px;
  padding: 12px;
  max-height: calc(100vh - 220px);
  overflow: auto;
}
.toc-list {
  list-style: none;
  margin: 0;
  padding-left: 0;
}
.toc-list > li {
  margin: 4px 0;
}
.toc-link {
  color: #374151;
  text-decoration: underline;
  text-underline-offset: 2px;
}
.toc-link:hover {
  color: #111827;
}
.toc-sub {
  list-style: none;
  margin: 4px 0 0 12px;
  padding-left: 0;
}
.rfm-footer {
  position: relative;
}
.reviewer-inline {
  flex: 1 1 620px;
  min-width: 460px;
  max-width: 760px;
}
@media (max-width: 768px) {
  .reviewer-inline {
    flex: 1 1 auto;
    min-width: 0;
  }
}
.reviewer-inline :deep(.vs__dropdown-toggle) {
  min-height: 38px;
}
.reviewer-inline :deep(.vs__dropdown-menu),
.reviewer-inline :deep(.vs__dropdown-option) {
  font-size: 0.8125rem;
}
</style>

<style>
.vs__dropdown-menu {
  z-index: 2055 !important;
}
.select-opens-up .vs__dropdown-menu {
  top: auto !important;
  bottom: 100% !important;
  margin-bottom: 4px;
}
</style>
